import React, { useEffect, useState } from 'react';
import { Input } from 'reactstrap';
import cs_analysis_fetch from '../../../utils/cs_analysis_fetch';
import { DECLINE_PROMPT, TRIAGE_PATHS, merge_candidates, merge_confirm_text } from './ticket_triage';

/**
 * Triage strip for a client-raised ticket that is still PENDING (Nikhil
 * 2026-09-13): Accept · Merge into… · Decline…. Rendered on the Client
 * Tickets queue row (compact) and at the top of the ticket drawer.
 *
 * Merge searches the org's open tickets (GET /tickets?storeId= — the same
 * list the org tab shows, filtered client-side; there is no server search)
 * and confirms before POSTing. Decline reuses the client-safe reason prompt.
 * Every click stops propagation: the queue row opens the drawer on click.
 * Built against the contract before it shipped — an older backend 404s the
 * routes and the error shows inline; nothing else on the row is affected.
 */

const btn = (variant) => ({
    fontSize: 11, padding: '1px 9px', borderRadius: 10, cursor: 'pointer', lineHeight: '17px', fontWeight: 600, whiteSpace: 'nowrap',
    ...(variant === 'accept' ? { background: '#2e7d4f', border: '1px solid #2e7d4f', color: '#fff' }
        : variant === 'decline' ? { background: '#fff', border: '1px solid #e08283', color: '#c0392b' }
            : { background: '#fff', border: '1px solid #c9d4de', color: '#3d4f5f' }),
});

export default function TriageStrip({ ticket, store_id, on_changed, compact }) {
    const [busy, set_busy] = useState(false);
    const [err, set_err] = useState(null);
    const [merging, set_merging] = useState(false);
    const [q, set_q] = useState('');
    const [candidates, set_candidates] = useState(null); // org tickets, loaded when merge opens

    useEffect(() => {
        if (!merging || candidates !== null || !store_id) return undefined;
        let dead = false;
        cs_analysis_fetch({ path: '/api/v1/tickets', query: { storeId: store_id } })
            .then((r) => { if (!dead) set_candidates((r && r.tickets) || []); })
            .catch((e) => { if (!dead) { set_candidates([]); set_err((e && e.message) || 'could not load the org tickets'); } });
        return () => { dead = true; };
    }, [merging, candidates, store_id]);

    const stop = (e) => { if (e) { e.stopPropagation(); } };
    const run = async (fn) => {
        if (busy) return;
        set_busy(true);
        set_err(null);
        try {
            await fn();
            if (on_changed) await on_changed();
        } catch (e) { set_err((e && e.message) || 'action failed'); }
        set_busy(false);
    };
    const accept = (e) => { stop(e); run(() => cs_analysis_fetch({ path: TRIAGE_PATHS.accept(ticket.id), method: 'POST' })); };
    const decline = (e) => {
        stop(e);
        const reason = window.prompt(DECLINE_PROMPT);
        if (!reason || !reason.trim()) return;
        run(() => cs_analysis_fetch({ path: TRIAGE_PATHS.decline(ticket.id), method: 'POST', body: { reason: reason.trim() } }));
    };
    const merge_into = (e, into) => {
        stop(e);
        if (!window.confirm(merge_confirm_text(ticket, into))) return;
        run(async () => {
            await cs_analysis_fetch({ path: TRIAGE_PATHS.merge(ticket.id), method: 'POST', body: { intoId: String(into.id) } });
            set_merging(false);
            set_q('');
        });
    };
    const matches = merging ? merge_candidates(candidates || [], q, ticket.id).slice(0, 20) : [];

    return (
        <div onClick={stop} style={{ marginTop: compact ? 5 : 0, fontSize: compact ? 11 : 12.5 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span title='Raised by the client — accept it, merge it into an existing ticket, or decline it'
                    style={{ color: '#8a6d1d', background: '#fff8e6', border: '1px solid #f0d48a', borderRadius: 10, padding: '0 8px', fontWeight: 600, whiteSpace: 'nowrap', lineHeight: '17px' }}>
                    <span role='img' aria-label='needs triage'>⚖</span> Needs triage
                </span>
                <button type='button' style={btn('accept')} disabled={busy} onClick={accept} title='Accept — it becomes a normal open ticket'>✓ Accept</button>
                <button type='button' style={btn(merging ? 'accept' : 'plain')} disabled={busy}
                    onClick={(e) => { stop(e); set_merging((v) => !v); set_q(''); }}
                    title='Merge into an existing ticket of this org (duplicate)'>
                    {merging ? '× cancel merge' : 'Merge into…'}
                </button>
                <button type='button' style={btn('decline')} disabled={busy} onClick={decline} title='Decline with a client-safe reason'>Decline…</button>
                {busy ? <span style={{ color: '#95a5a6' }}>…</span> : null}
            </div>
            {merging ? (
                <div style={{ position: 'relative', marginTop: 5 }}>
                    {!store_id ? (
                        <div style={{ color: '#95a5a6' }}>This row carries no org — open the ticket to merge it.</div>
                    ) : (
                        <>
                            <Input bsSize='sm' value={q} autoFocus placeholder="Search this org's open tickets (title / CT-ref / module)…"
                                onChange={(e) => set_q(e.target.value)}
                                onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Escape') { set_merging(false); set_q(''); } }}
                                onClick={stop}
                                disabled={busy}
                                style={{ maxWidth: 360, fontSize: 12 }} />
                            <div style={{ position: 'absolute', zIndex: 20, top: '100%', left: 0, minWidth: 320, maxWidth: 480, maxHeight: 240, overflowY: 'auto', background: '#fff', border: '1px solid #d7dce3', borderRadius: 6, boxShadow: '0 4px 14px rgba(0,0,0,0.1)', marginTop: 2 }}>
                                {matches.length === 0 ? (
                                    <div style={{ padding: '6px 10px', fontSize: 12, color: '#95a5a6' }}>
                                        {candidates === null ? 'Loading…' : q.trim() ? 'No open ticket of this org matches.' : 'No other open ticket in this org to merge into.'}
                                    </div>
                                ) : matches.map((t) => (
                                    <button key={t.id} type='button' disabled={busy} onClick={(e) => merge_into(e, t)}
                                        style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', borderBottom: '1px solid #f0f2f5', background: '#fff', padding: '6px 10px', cursor: 'pointer', fontSize: 12 }}>
                                        <div style={{ fontWeight: 600, color: '#2c3e50' }}>CT-{t.ref} {t.title}</div>
                                        <div style={{ color: '#7f8c8d', fontSize: 11 }}>{t.status}{t.priority ? ` · ${t.priority}` : ''}{t.module ? ` · ${t.module}` : ''}</div>
                                    </button>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            ) : null}
            {err ? <div style={{ color: '#c0392b', fontSize: 11, marginTop: 3 }}>{err}</div> : null}
        </div>
    );
}
